import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "../SchedulingStyles/AdminBookingTool.css";
import PatientSearch from "./PatientSearch";
import DisciplineDropdown from "./DisciplineDropdown";
import PractitionerDropdown from "./PractitionerDropdown";
import DateSelector from "./DateSelector";
import SlotSelector from "./SlotSelector";
import AppointmentFeeInput from "./AppointmentFeeInput";
import BookingModal from "./BookingModal";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getClinicId } from "../../../utils/config";
import FormatUrl from "../../../utils/UrlFormatter";

const AdminBookingTool = ({ user }) => {
  const { t } = useTranslation();
  const [csrfToken, setCsrfToken] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [breakEnabled, setBreakEnabled] = useState(true);
  const [breakSlot, setBreakSlot] = useState(null);
  const [bookedSlotIds, setBookedSlotIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [appointmentFee, setAppointmentFee] = useState("");
  const [resetPatientSearchKey, setResetPatientSearchKey] = useState(0);
  const [selectedDisciplineObj, setSelectedDisciplineObj] = useState(null);


  const clinicId = getClinicId(user);

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const csrfUrl = FormatUrl(`/csrf-token/`);
        const response = await fetch(csrfUrl, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          setCsrfToken(data.csrfToken);
        } else {
          console.error("Failed to fetch CSRF token:", response.status);
        }
      } catch (error) {
        console.error("Error fetching CSRF token:", error);
      }
    };

    fetchCsrfToken();
  }, []);

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const url = FormatUrl(`/get-clinic-disciplines/?clinic_id=${clinicId}`);
        const res = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
  
        const data = await res.json();
  
        if (data?.status === "Success" && Array.isArray(data?.disciplines)) {
          // const formatted = data.disciplines.map((item) => ({
          //   id: item.name,
          //   label: item.name,
          // }));
          // setDisciplines(formatted);
          setDisciplines(data.disciplines);
        } else {
          setDisciplines([]);
        }
      } catch {
        setDisciplines([]);
      }
    };
  
    fetchDisciplines();
  }, [clinicId]);
  
  const handleSlotSelection = useCallback((slots, breakFlag, breakSlotObj) => {
    setSelectedSlots(slots);
    setBreakEnabled(breakFlag);
    setBreakSlot(breakSlotObj);
  }, []);
  

  const handleConfirmBooking = () => {
    setShowModal(false);

    const selectedIds = selectedSlots.map((slot) => slot.id);
    const updatedIds = [...selectedIds];

    if (breakEnabled && breakSlot) {
      updatedIds.push(breakSlot.id);
    }

    const updatedSet = new Set([...bookedSlotIds, ...updatedIds]);
    setBookedSlotIds(Array.from(updatedSet));

    // ✅ Reset all states after booking
    setSelectedPatient(null);
    setSelectedDiscipline("");
    setSelectedPractitioner(null);
    setSelectedDate("");
    setSelectedSlots([]);
    setBreakSlot(null);
    setBreakEnabled(true);
    setAppointmentFee("");
    setResetPatientSearchKey((prev) => prev + 1);
  };

  const handleDisciplineChange = (discipline) => {
    setSelectedDiscipline(discipline);
    const fullObj = disciplines.find((d) => d.name === discipline);
    setSelectedDisciplineObj(fullObj || null);
    setSelectedPractitioner(null);
    setSelectedDate("");
    setSelectedSlots([]);
    setBreakSlot(null);
    setShowModal(false);
  };

  const handlePractitionerChange = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setSelectedDate("");
    setSelectedSlots([]);
    setBreakSlot(null);
    setShowModal(false);
  };

  const handlePatientSelect = useCallback((patient) => {
    setSelectedPatient(patient);
  }, []);

  return (
    <div className="admin-booking-page">
      <div className="admin-booking-wrapper">
        <h2>{t("admin_scheduling_new.title")}</h2>
        <p>{t("admin_scheduling_new.subtitle")}</p>

        <PatientSearch
          onSelect={handlePatientSelect}
          clinicId={clinicId}
          csrfToken={csrfToken}
          resetTrigger={resetPatientSearchKey}
        />

        {selectedPatient && (
          <DisciplineDropdown
          disciplines={disciplines.map((d) => ({ id: d.name, label: d.name }))}
          onSelect={handleDisciplineChange}
        />             
        )}

        {selectedDiscipline && (
          <PractitionerDropdown
            discipline={selectedDiscipline}
            clinicId={clinicId}
            csrfToken={csrfToken}
            onSelect={handlePractitionerChange}
          />
        )}

        {selectedPractitioner && (
          <p className="prac-status">
            {t("admin_scheduling_new.practitioner_type")}{" "}
            {selectedPractitioner.is_registered
              ? t("admin_scheduling_new.registered_label")
              : t("admin_scheduling_new.not_registered_label")}
          </p>
        )}

        {selectedPractitioner && (
          <DateSelector
            clinicId={clinicId}
            practitionerId={selectedPractitioner?.id}
            csrfToken={csrfToken}
            onSelect={setSelectedDate}
          />
        )}

        {selectedDate && (
          <SlotSelector
            clinicId={clinicId}
            practitionerId={selectedPractitioner?.id}
            selectedDate={selectedDate}
            csrfToken={csrfToken}
            bookedSlotIds={bookedSlotIds}
            onSelect={handleSlotSelection}
            discipline={selectedDiscipline}
            breakLength={selectedDisciplineObj?.break_length ?? null}
          />
        )}

        {selectedSlots.length > 0 && (
          <>
            <AppointmentFeeInput
              value={appointmentFee}
              onChange={setAppointmentFee}
            />
            <button className="proceed-button" onClick={() => setShowModal(true)}>
              {t("admin_scheduling_new.proceed_to_book")}
            </button>
          </>
        )}

        <BookingModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmBooking}
          details={{
            patient: selectedPatient,
            discipline: selectedDiscipline,
            practitioner: selectedPractitioner,
            date: selectedDate,
            slots: selectedSlots,
            breakEnabled: breakEnabled,
            breakSlot: breakSlot,
            clinicId: clinicId,
            csrfToken: csrfToken,
            appointmentFee,
            isRegistered: selectedPractitioner?.is_registered,
          }}
        />
      </div>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default AdminBookingTool;
